import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Fingerprint, MapPin, ShieldCheck } from 'lucide-react';
import useLiveClock from './useLiveClock';
import SignatureCanvasModal from './SignatureCanvasModal';

/**
 * Hero com relógio ao vivo e simulador de início de jornada.
 * @param {{ onRegistered: () => void, registered: boolean }} props
 */
const HeroSimuladorPonto = ({ onRegistered, registered }) => {
    const { time, dateLabel } = useLiveClock();
    const [signatureOpen, setSignatureOpen] = useState(false);

    return (
        <section className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.16),_transparent_55%)] dark:bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.18),_transparent_50%)]" />

            <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-14 lg:px-8 lg:pt-16">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                    className="mx-auto max-w-3xl text-center"
                >
                    <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Módulo de ponto eletrônico
                    </span>
                    <h1 className="mt-5 text-3xl font-black tracking-tight text-slate-900 dark:text-white sm:text-5xl">
                        Planos de Registro de Ponto
                    </h1>
                    <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
                        Simule a marcação da jornada com hora real, geolocalização e assinatura digital.
                        O mesmo fluxo que o DP e a contabilidade passam a auditar sem papel.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.08 }}
                    className="mx-auto mt-10 max-w-xl rounded-3xl border border-white/70 bg-white/90 p-6 shadow-xl shadow-emerald-950/5 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90 sm:p-8"
                >
                    <p className="text-center text-[11px] font-semibold tracking-[0.18em] text-emerald-700 dark:text-emerald-300 sm:text-xs">
                        {dateLabel}
                    </p>
                    <p
                        className="mt-3 text-center font-mono text-5xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-white sm:text-7xl"
                        aria-label={`Hora atual ${time}`}
                    >
                        {time}
                    </p>

                    <div className="relative mx-auto mt-8 flex h-28 w-28 items-center justify-center">
                        <motion.span
                            className="absolute inset-0 rounded-full border border-emerald-400/70"
                            animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0, 0.7] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut' }}
                            aria-hidden="true"
                        />
                        <motion.span
                            className="absolute inset-2 rounded-full bg-emerald-500/10"
                            animate={{ opacity: [0.45, 0.9, 0.45] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                            aria-hidden="true"
                        />
                        <div className="relative flex h-24 w-24 items-center justify-center rounded-full border-2 border-emerald-500 bg-white text-emerald-500 shadow-[0_0_32px_rgba(16,185,129,0.45)] dark:bg-slate-950">
                            <Fingerprint className="h-12 w-12" strokeWidth={1.75} />
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col items-center gap-3 md:flex-row md:justify-center">
                        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            <MapPin className="h-4 w-4 text-emerald-500" />
                            Registro validado via GPS
                        </span>
                        <span className="inline-flex items-center gap-2 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700 dark:text-slate-200 sm:text-xs">
                            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
                            Registro com assinatura digital
                        </span>
                    </div>

                    {registered && (
                        <p className="mt-5 rounded-2xl bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200">
                            Jornada de demonstração registrada com assinatura digital.
                        </p>
                    )}

                    <div className="mt-6 flex flex-col items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setSignatureOpen(true)}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg transition duration-300 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-emerald-500/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 sm:w-auto"
                        >
                            <Fingerprint className="h-5 w-5" />
                            Iniciar Jornada
                        </button>
                        <button
                            type="button"
                            onClick={() => document.getElementById('oferta')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            className="text-sm font-medium text-slate-500 underline-offset-4 transition hover:text-emerald-600 hover:underline dark:text-slate-400"
                        >
                            Ver preço do módulo
                        </button>
                    </div>
                </motion.div>

                <ol className="mx-auto mt-8 grid max-w-3xl gap-3 sm:grid-cols-3">
                    {[
                        'Confira hora e local',
                        'Assine digitalmente',
                        'Jornada registrada',
                    ].map((step, index) => (
                        <li
                            key={step}
                            className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/70 px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-200"
                        >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-xs font-bold text-white">
                                {index + 1}
                            </span>
                            {step}
                        </li>
                    ))}
                </ol>
            </div>

            <SignatureCanvasModal
                open={signatureOpen}
                onClose={() => setSignatureOpen(false)}
                onRegistered={onRegistered}
            />
        </section>
    );
};

export default HeroSimuladorPonto;
